/* Anania Immersive v7.3 PROD — cached DOM, conditional scroll work, native scrolling. */
(() => {
  'use strict';

  const rootEl = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const purchaseDock = window.matchMedia('(min-width: 750px)');
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  let raf = 0;
  let revealObserver = null;
  let priceObserver = null;
  let globalBound = false;
  let scrollBound = false;
  let resizeFrame = 0;
  let lastLayoutWidth = window.innerWidth;
  let lastLayoutHeight = window.innerHeight;

  const state = {
    hero: null,
    stories: [],
    productStories: [],
    floating: null,
    mainProduct: null,
    buyButton: null,
    stickyOffset: 0,
    heroDynamic: false,
    heroProgress: -1,
    dockVisible: null,
  };

  const setStickyOffset = () => {
    const header = document.querySelector('sticky-header');
    const height = header ? Math.max(0, Math.round(header.offsetHeight)) : 0;
    if (height === state.stickyOffset) return;
    state.stickyOffset = height;
    rootEl.style.setProperty('--aq-sticky-offset', `${height}px`);
  };

  const mountPurchaseDock = (scope = document) => {
    const candidate = scope.querySelector?.('[data-aq-product-float]');
    const mounted = document.body.querySelector(':scope > [data-aq-product-float][data-aq-product-float-mounted="true"]');
    if (!candidate) return mounted;
    if (candidate === mounted) return mounted;
    mounted?.remove();
    candidate.dataset.aqProductFloatMounted = 'true';
    candidate.setAttribute('aria-hidden', 'true');
    candidate.setAttribute('inert', '');
    document.body.appendChild(candidate);
    return candidate;
  };

  const initReveal = (scope = document) => {
    const items = qsa('[data-aq-reveal]', scope).filter((el) => el.dataset.aqRevealBound !== 'true');
    if (!items.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => {
        el.dataset.aqRevealBound = 'true';
        el.classList.add('is-inview');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
    }

    items.forEach((el) => {
      el.dataset.aqRevealBound = 'true';
      revealObserver.observe(el);
    });
  };

  const makeStoryState = (story, stepSelector, visualSelector, counterSelector = null) => ({
    story,
    steps: qsa(stepSelector, story),
    visuals: qsa(visualSelector, story),
    counter: counterSelector ? story.querySelector(counterSelector) : null,
    activeIndex: -1,
    lastProgress: -1,
  });

  const refreshNodes = () => {
    const previousFloating = state.floating;
    state.hero = document.querySelector('[data-aq-cinematic-hero]');
    state.stories = qsa('[data-aq-story]').map((story) => makeStoryState(
      story,
      '[data-aq-story-step]',
      '[data-aq-story-visual]',
      '[data-aq-story-count]'
    ));
    state.productStories = qsa('[data-aq-product-story]').map((story) => makeStoryState(
      story,
      '[data-aq-product-step]',
      '[data-aq-product-visual]'
    ));
    state.floating = document.querySelector('[data-aq-product-float]');
    if (state.floating !== previousFloating) state.dockVisible = null;
    state.mainProduct = document.querySelector('.template-product .aq-product-page');
    state.buyButton = state.mainProduct?.querySelector('.product-form__submit, product-form button[type="submit"], button[name="add"]') || null;
  };

  const activateClosest = (entry, anchor) => {
    const { story, steps, visuals, counter } = entry;
    if (!steps.length) return;

    let active = 0;
    let distance = Infinity;
    let firstRect = null;
    let lastRect = null;

    steps.forEach((step, index) => {
      const rect = step.getBoundingClientRect();
      if (index === 0) firstRect = rect;
      if (index === steps.length - 1) lastRect = rect;
      const d = Math.abs((rect.top + rect.height * 0.5) - anchor);
      if (d < distance) {
        distance = d;
        active = index;
      }
    });

    if (active !== entry.activeIndex) {
      entry.activeIndex = active;
      steps.forEach((step, index) => step.classList.toggle('is-active', index === active));
      visuals.forEach((visual, index) => visual.classList.toggle('is-active', index === active));
      if (counter) counter.textContent = `${String(active + 1).padStart(2, '0')} / ${String(steps.length).padStart(2, '0')}`;
    }

    if (firstRect && lastRect) {
      const span = Math.max(1, lastRect.bottom - firstRect.top);
      const progress = Math.round(clamp((anchor - firstRect.top) / span) * 200) / 200;
      if (progress !== entry.lastProgress) {
        entry.lastProgress = progress;
        story.style.setProperty('--aq-progress', Math.max(.02, progress).toFixed(3));
      }
    }
  };

  const updateHero = (viewportHeight) => {
    const hero = state.hero;
    if (!hero) return;

    if (reduced.matches || !desktop.matches) {
      if (state.heroDynamic) {
        ['--aq-hero-media-scale', '--aq-hero-media-y', '--aq-hero-copy-y', '--aq-hero-copy-opacity'].forEach((name) => hero.style.removeProperty(name));
        state.heroDynamic = false;
        state.heroProgress = -1;
      }
      return;
    }

    const rect = hero.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) return;
    const progress = Math.round(clamp((state.stickyOffset - rect.top) / Math.max(1, rect.height * 1.12)) * 80) / 80;
    if (progress === state.heroProgress) return;
    state.heroProgress = progress;
    state.heroDynamic = true;
    hero.style.setProperty('--aq-hero-media-scale', (1 + progress * .075).toFixed(4));
    hero.style.setProperty('--aq-hero-media-y', `${(progress * 44).toFixed(1)}px`);
    hero.style.setProperty('--aq-hero-copy-y', `${(-progress * 28).toFixed(1)}px`);
    hero.style.setProperty('--aq-hero-copy-opacity', (1 - progress * .42).toFixed(3));
  };

  const isViewportVisible = (rect, topInset = 0, bottomInset = 0) => {
    if (!rect || rect.width <= 0 || rect.height <= 0) return false;
    return rect.bottom > topInset && rect.top < (window.innerHeight - bottomInset);
  };

  const setPurchaseDockVisible = (floating, show) => {
    if (!floating || state.dockVisible === show) return;
    state.dockVisible = show;
    floating.classList.toggle('is-visible', show);
    floating.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) floating.removeAttribute('inert');
    else floating.setAttribute('inert', '');
  };

  const updateStories = (viewportHeight) => {
    const anchor = state.stickyOffset + Math.max(1, viewportHeight - state.stickyOffset) * 0.5;

    state.stories.forEach((entry) => {
      const rect = entry.story.getBoundingClientRect();
      if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return;
      activateClosest(entry, anchor);
    });

    state.productStories.forEach((entry) => {
      const rect = entry.story.getBoundingClientRect();
      if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return;
      activateClosest(entry, anchor);
    });

    const floating = state.floating;
    const buyButton = state.buyButton;
    if (!floating || !buyButton) return;

    const buyRect = buyButton.getBoundingClientRect();
    const buyRendered = buyRect.width > 0 && buyRect.height > 0;
    const buyVisible = isViewportVisible(buyRect, state.stickyOffset + 8, 18);
    const scrolledPastBuy = buyRendered && buyRect.bottom <= state.stickyOffset + 8;
    const buyEnabled = buyRendered && !buyButton.disabled && buyButton.getAttribute('aria-disabled') !== 'true';
    const blocked = document.body.classList.contains('overflow-hidden') || document.body.classList.contains('menu-opening');
    setPurchaseDockVisible(floating, purchaseDock.matches && !blocked && buyEnabled && scrolledPastBuy && !buyVisible);
  };

  const frame = () => {
    raf = 0;
    const viewportHeight = window.innerHeight;
    updateHero(viewportHeight);
    updateStories(viewportHeight);
  };

  const queue = () => {
    if (raf || document.hidden) return;
    raf = requestAnimationFrame(frame);
  };

  const hasScrollWork = () => Boolean(
    state.hero || state.stories.length || state.productStories.length || (state.floating && state.buyButton)
  );

  const ensureScrollBinding = () => {
    if (scrollBound || !hasScrollWork()) return;
    scrollBound = true;
    window.addEventListener('scroll', queue, { passive: true });
  };

  const syncLivePrice = () => {
    const priceRoot = state.mainProduct?.querySelector('[id^="price-"] .price');
    if (!priceRoot) return;
    const selector = priceRoot.classList.contains('price--on-sale')
      ? '.price__sale .price-item--sale'
      : '.price__regular .price-item--regular';
    const source = priceRoot.querySelector(selector) || priceRoot.querySelector('.price-item--sale, .price-item--regular');
    const text = source?.textContent?.replace(/\s+/g, ' ').trim();
    if (!text) return;
    qsa('[data-aq-live-price]').forEach((target) => { target.textContent = text; });
  };

  const initPriceSync = () => {
    priceObserver?.disconnect();
    priceObserver = null;
    const priceRoot = state.mainProduct?.querySelector('[id^="price-"]');
    if (!priceRoot || !('MutationObserver' in window)) {
      syncLivePrice();
      return;
    }
    priceObserver = new MutationObserver(syncLivePrice);
    priceObserver.observe(priceRoot, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    syncLivePrice();
  };

  const bindPurchaseLinks = () => {
    if (rootEl.dataset.aqPurchaseBound === 'true') return;
    rootEl.dataset.aqPurchaseBound = 'true';
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-aq-scroll-buy]');
      if (!trigger) return;
      const target = state.buyButton || document.querySelector('#AQProductBuy') || state.mainProduct;
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' });
    });
  };

  const bindGlobal = () => {
    if (globalBound) return;
    globalBound = true;
    window.addEventListener('resize', () => {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        const width = window.innerWidth;
        const height = window.innerHeight;
        const widthChanged = Math.abs(width - lastLayoutWidth) > 4;
        const heightChanged = Math.abs(height - lastLayoutHeight) > 96;
        // Mobile browser chrome can emit many resize events during a normal
        // scroll. Ignore tiny height-only changes so they don't compete with
        // scrolling/compositing; still react to rotations and real resizes.
        if (!widthChanged && !heightChanged) return;
        lastLayoutWidth = width;
        lastLayoutHeight = height;
        if (widthChanged) {
          setStickyOffset();
          refreshNodes();
          ensureScrollBinding();
        }
        queue();
      });
    }, { passive: true });
    window.addEventListener('orientationchange', () => {
      window.setTimeout(() => {
        lastLayoutWidth = window.innerWidth;
        lastLayoutHeight = window.innerHeight;
        setStickyOffset();
        refreshNodes();
        ensureScrollBinding();
        queue();
      }, 80);
    }, { passive: true });
    window.addEventListener('pageshow', queue, { passive: true });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) queue(); });
    reduced.addEventListener?.('change', () => { initReveal(document); queue(); });
    desktop.addEventListener?.('change', queue);
    purchaseDock.addEventListener?.('change', queue);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => { refreshNodes(); initPriceSync(); queue(); });
    }
  };

  const init = (scope = document) => {
    setStickyOffset();
    mountPurchaseDock(scope);
    initReveal(scope);
    refreshNodes();
    bindPurchaseLinks();
    initPriceSync();
    bindGlobal();
    ensureScrollBinding();
    rootEl.classList.add('aq-enhanced');
    queue();
  };

  const safeInit = (scope = document) => {
    try {
      init(scope);
    } catch (error) {
      rootEl.classList.remove('aq-enhanced');
      console.error('[Anania] Immersive enhancement disabled after an initialization error.', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => safeInit(document), { once: true });
  } else {
    safeInit(document);
  }

  document.addEventListener('shopify:section:load', (event) => safeInit(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target?.querySelector?.('[data-aq-product-story]')) {
      document.body.querySelector(':scope > [data-aq-product-float][data-aq-product-float-mounted="true"]')?.remove();
    }
    refreshNodes();
    queue();
  });
  document.addEventListener('shopify:section:reorder', () => { refreshNodes(); queue(); });
  document.addEventListener('shopify:block:select', queue);
})();
